import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { Button, IconButton, Typography } from '@mui/material';
import { type ComponentProps, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { usePublishReport } from '@/admins/SmartReports/hooks/usePublishReport';
import { useUpdateReportVersion } from '@/admins/SmartReports/hooks/useUpdateReportVersion';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { combineSx, sxVariants } from '@/utils/combineSx';
import { ADMINS_SMART_REPORTS_PATH } from '@/utils/constants';
import { useEditSmartReport } from './EditSmartReportContext';
import { ViewReportButton } from './ViewReportButton';

const PublishButton = () => {
  const history = useHistory();
  const { report } = useEditSmartReport();
  const {
    publishReport,
    publishedReport,
    error: publishingError,
    loading: publishLoading,
  } = usePublishReport();

  const { setSnack } = useSnackBarContext();

  useEffect(() => {
    if (publishedReport?.id) {
      setSnack({
        message: 'Report published successfully',
        severity: 'success',
        open: true,
      });

      // Redirect to the report list page
      history.push(ADMINS_SMART_REPORTS_PATH);
    }
  }, [history.push, publishedReport, setSnack]);

  useEffect(() => {
    if (publishingError) {
      setSnack({
        message: 'Error publishing report. Please try again.',
        severity: 'error',
        open: true,
      });
    }
  }, [publishingError, setSnack]);

  return (
    <Button
      variant='contained'
      color='secondary'
      disabled={publishLoading}
      onClick={() => publishReport(report)}
    >
      Publish
    </Button>
  );
};

const DraftButton = () => {
  const { reportVersion, pages } = useEditSmartReport();

  const {
    updateReportVersion,
    updatedReportVersion,
    error: versionUpdateError,
    loading: versionUpdateLoading,
  } = useUpdateReportVersion();
  const { setSnack } = useSnackBarContext();

  const onUpdateReportVersion = useCallback(() => {
    updateReportVersion({ ...reportVersion, pages });
  }, [reportVersion, pages, updateReportVersion]);

  useEffect(() => {
    if (versionUpdateError) {
      setSnack({
        message: 'Error updating report version. Please try again.',
        severity: 'error',
        open: true,
      });
    }
  }, [versionUpdateError, setSnack]);

  useEffect(() => {
    if (updatedReportVersion?.id) {
      setSnack({
        message: 'Smart Report draft successfully saved.',
        severity: 'success',
        open: true,
      });
    }
  }, [updatedReportVersion, setSnack]);

  return (
    <Button
      color='secondary'
      variant='outlined'
      onClick={onUpdateReportVersion}
      disabled={versionUpdateLoading}
    >
      Save Draft
    </Button>
  );
};

export const Actions = () => {
  return (
    <>
      <ViewReportButton />
      <DraftButton />
      <PublishButton />
    </>
  );
};

const buttonToggleDrawerStyle = sxVariants({
  base: {
    position: 'absolute',
    top: '1rem',
    right: '0',
    zIndex: 20,
    backgroundColor: 'background.paper',
    boxShadow: '0 1px 1px rgba(0,0,0,0.11)',
    display: {
      xs: 'inline-flex',
      lg: 'none',
    },
    transition: 'translate 0.2s var(--ease-move)',

    '& > svg': {
      transition: 'rotate 0.2s 0.2s var(--ease-move)',
    },
  },
  variants: {
    isDrawerOpen: {
      true: {
        translate: 'calc(clamp(15rem, 20%, 30rem) * -1) 0 0',
        '& > svg': {
          rotate: '180deg',
        },
      },
      false: {
        translate: '-1rem 0 0',
        '& > svg': {
          rotate: '0deg',
        },
      },
    },
  },
});

type ComponentsDrawerButtonProps = Omit<ComponentProps<typeof IconButton>, 'onClick'> & {
  isDrawerOpen: boolean;
};
export const ComponentsDrawerButton = ({
  sx,
  isDrawerOpen,
  ...props
}: ComponentsDrawerButtonProps) => {
  const { toggleDrawer } = useEditSmartReport();

  return (
    <IconButton
      {...props}
      sx={combineSx(buttonToggleDrawerStyle({ isDrawerOpen }), sx)}
      onClick={toggleDrawer}
    >
      <MenuOpenIcon />
    </IconButton>
  );
};

export const BackToReportsButton = () => {
  return (
    <Button
      variant='text'
      color='secondary'
      href={ADMINS_SMART_REPORTS_PATH}
      startIcon={<ArrowBackIcon />}
      sx={{ alignSelf: 'start' }}
    >
      <Typography variant='body1' sx={{ color: 'secondary' }}>
        Back to reports
      </Typography>
    </Button>
  );
};
